import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchAvailability, weekStartOf, type AvailabilityRequest } from './availability-api';

/**
 * Nested under one prefix (STYLE.md), so `invalidateQueries({ queryKey: availabilityQueryKeys.all })`
 * refreshes every week on screen — what a host does after a hold is refused or a save conflicts.
 */
export const availabilityQueryKeys = {
    all: ['availability'] as const,
    week: (request: AvailabilityRequest) =>
        [
            'availability',
            'week',
            {
                brandId: request.brandId,
                teamMemberId: request.teamMemberId,
                // Team mode (ROBO-694). `undefined` entries are dropped when the key is hashed,
                // so a single-member key is exactly what it was.
                teamMemberIds: request.teamMemberIds ? [...request.teamMemberIds] : undefined,
                team: request.team || undefined,
                weekMask: request.weekMask || undefined,
                favoriteIds:
                    request.favoriteIds && request.favoriteIds.length > 0
                        ? [...request.favoriteIds]
                        : undefined,
                duration: request.duration,
                weekStart: weekStartOf(request.weekStart),
                latitude: request.location?.latitude ?? null,
                longitude: request.location?.longitude ?? null,
                ignoreTaskIds: [...(request.ignoreTaskIds ?? [])],
                isGuest: Boolean(request.downloadGuid)
            }
        ] as const
};

/**
 * One week of one member's availability, or of a team's.
 *
 * ★ THE CALLER'S HOLD IDS ARE NOT PART OF THE KEY, on purpose. They change every time a slot is
 * picked, and keying on them would refetch (and redraw) the week on every pick. They are read at
 * fetch time instead, so whatever refetch does happen — paging, an invalidation — sends the current
 * ones, which is what `cart-gcal.js` does (`buildCalendars` collects them once per rebuild).
 */
export function useAvailabilityWeek(request: AvailabilityRequest, enabled = true) {
    const latest = useRef(request);
    useEffect(() => {
        latest.current = request;
    });

    // Deliberate: the hold ids are read at fetch time and kept OUT of the key (see above).
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    return useQuery({
        queryKey: availabilityQueryKeys.week(request),
        queryFn: () =>
            fetchAvailability({
                ...request,
                ignoreReservationIds: latest.current.ignoreReservationIds
            }),
        enabled,
        // Availability moves as other people book; never serve a week from an earlier visit.
        staleTime: 0,
        throwOnError: false
    });
}
